import React, { useState, useEffect, useMemo } from "react";
import Navbar from "../Components/Navbar";
import Footer from "../Components/Footer";
import { toast } from "react-toastify";
import { API_URI } from "../Common/data";
import Loader from "../loader/Loader";

function FAQ() {
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedFaqId, setSelectedFaqId] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [faqs, setFaqs] = useState([]);

  useEffect(() => {
    setIsLoading(true);
    fetch(`${API_URI}/faq`)
      .then((res) => res.json())
      .then((data) => {
        setFaqs(data?.data||[]);
        setIsLoading(false);
      })
      .catch((err) => {
        toast.error(err.message);
        setIsLoading(false);
      });
  }, []);

  const categorizeFaq = (faq) => {
    const haystack = `${faq.question} ${faq.answer}`.toLowerCase();

    if (
      haystack.includes("book") ||
      haystack.includes("booking") ||
      haystack.includes("slot") ||
      haystack.includes("reserve")
    ) {
      return "Booking";
    }
    if (
      haystack.includes("cake") ||
      haystack.includes("food") ||
      haystack.includes("bouquet") ||
      haystack.includes("karaoke") ||
      haystack.includes("add-on") ||
      haystack.includes("package")
    ) {
      return "Add-Ons";
    }
    if (
      haystack.includes("cancel") ||
      haystack.includes("refund") ||
      haystack.includes("reschedule") ||
      haystack.includes("policy")
    ) {
      return "Policies";
    }
    if (
      haystack.includes("wheelchair") ||
      haystack.includes("parking") ||
      haystack.includes("location") ||
      haystack.includes("reach")
    ) {
      return "Venue";
    }

    return "General";
  };

  const normalizedFaqs = useMemo(() => {
    return faqs.map((faq, index) => ({
      ...faq,
      faqId: faq._id || `${index}-${faq.question}`,
      category: categorizeFaq(faq),
    }));
  }, [faqs]);

  const categories = useMemo(() => {
    return ["All", ...new Set(normalizedFaqs.map((faq) => faq.category))];
  }, [normalizedFaqs]);

  const filteredFaqs = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();

    return normalizedFaqs.filter((faq) => {
      const matchesCategory =
        selectedCategory === "All" || faq.category === selectedCategory;
      const matchesSearch =
        !query ||
        faq.question.toLowerCase().includes(query) ||
        faq.answer.toLowerCase().includes(query);

      return matchesCategory && matchesSearch;
    });
  }, [normalizedFaqs, searchQuery, selectedCategory]);

  const selectedFaq = useMemo(() => {
    return (
      filteredFaqs.find((faq) => faq.faqId === selectedFaqId) || filteredFaqs[0] || null
    );
  }, [filteredFaqs, selectedFaqId]);

  useEffect(() => {
    if (!filteredFaqs.length) {
      setSelectedFaqId(null);
      return;
    }

    const stillVisible = filteredFaqs.some((faq) => faq.faqId === selectedFaqId);
    if (!stillVisible) {
      setSelectedFaqId(filteredFaqs[0].faqId);
    }
  }, [filteredFaqs, selectedFaqId]);

  if (isLoading) {
    return <Loader />;
  }

  return (
    <>
      <Navbar />
      <div className="news-banner news-banner-faq">
        <p>FAQ</p>
        <br />
        <h1> Everything You Need to Know</h1>
      </div>

      <div className="faq-div">
        <div className="faq-revamp-intro">
          <div>
            <p className="faq-revamp-intro__eyebrow">Quick Help</p>
            <h2>Questions people usually ask before they book.</h2>
            <p className="faq-revamp-intro__text">
              Browse the essentials around private screenings, celebration setups,
              and how the Cine Celebrate experience works from inquiry to event day.
            </p>
          </div>
          <div className="faq-revamp-intro__meta">
            <span>Live answers</span>
            <strong>{faqs.length} topics</strong>
          </div>
        </div>
        <div className="faq-toolbar">
          <div className="faq-categories" role="tablist" aria-label="FAQ categories">
            {categories.map((category) => (
              <button
                key={category}
                type="button"
                className={`faq-categories__button ${
                  selectedCategory === category ? "is-active" : ""
                }`}
                onClick={() => setSelectedCategory(category)}
              >
                {category}
              </button>
            ))}
          </div>
          <label className="faq-search" htmlFor="faq-search">
            <span>Search</span>
            <input
              id="faq-search"
              type="text"
              placeholder="Search questions or answers"
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
            />
          </label>
        </div>

        <div className="faq-browser">
          <div className="faq-list" role="listbox" aria-label="FAQ questions">
            {filteredFaqs.length ? (
              filteredFaqs.map((faq, index) => (
                <div
                  key={faq.faqId}
                  className={`faq-list__entry ${
                    selectedFaq?.faqId === faq.faqId ? "is-active" : ""
                  }`}
                >
                  <button
                    type="button"
                    className={`faq-list__item ${
                      selectedFaq?.faqId === faq.faqId ? "is-active" : ""
                    }`}
                    onClick={() => setSelectedFaqId(faq.faqId)}
                  >
                    <span className="faq-list__count">
                      {String(index + 1).padStart(2, "0")}
                    </span>
                    <div className="faq-list__copy">
                      <span className="faq-list__category">{faq.category}</span>
                      <strong>{faq.question}</strong>
                    </div>
                  </button>
                  <div className="faq-list__mobile-answer">
                    <p>{faq.answer}</p>
                  </div>
                </div>
              ))
            ) : (
              <div className="faq-empty">
                <p>No questions match your search right now.</p>
              </div>
            )}
          </div>

          <div className="faq-detail">
            {selectedFaq ? (
              <>
                <p className="faq-detail__eyebrow">{selectedFaq.category}</p>
                <h3>{selectedFaq.question}</h3>
                <p>{selectedFaq.answer}</p>
              </>
            ) : (
              <div className="faq-empty faq-empty--detail">
                <p>Select a question to see the answer.</p>
              </div>
            )}
          </div>
        </div>
      </div>

      <Footer />
    </>
  );
}

export default FAQ;
